/*
 * This refreshing session implementation checks the expiration given an external function
 * and issues a refresh command on the connector.
 * This implementation differs from the Backstage one since there is no login popup and
 * the refresh operation takes the session as input.
 */

import {
  SessionManager,
  SessionShouldRefreshFunc,
  GetSessionOptions,
  AuthConnector,
} from './types';
import { SessionStateTracker } from './SessionStateTracker';
import { GuestUsersAuthSession } from '../apis/guest_users/GuestUsersAuth';
import { CookieSessionService } from './CookieSessionService';

type Options<T> = {
  /** The connector used for acting on the auth session */
  connector: AuthConnector<T>;
  /** Storage key to use to store sessions */
  storageKey: string;
  /** Used to check if the session needs to be refreshed */
  sessionShouldRefresh: SessionShouldRefreshFunc<T>;
  /** Cookie service for session storage (optional, falls back to localStorage) */
  cookieService?: CookieSessionService;
};

/**
 * RefreshingAuthSessionManager manages an underlying session that has
 * and expiration time and needs to be refreshed periodically.
 */
export class RefreshingAuthSessionManager<T> implements SessionManager<T> {
  private readonly connector: AuthConnector<T>;
  private readonly storageKey: string;
  private readonly sessionShouldRefreshFunc: SessionShouldRefreshFunc<T>;
  private readonly stateTracker = new SessionStateTracker();
  private readonly cookieService?: CookieSessionService;

  private refreshPromise?: Promise<T>;
  private currentSession: T | undefined;

  constructor(options: Options<T>) {
    const { connector, storageKey, sessionShouldRefresh, cookieService } =
      options;

    this.connector = connector;
    this.storageKey = storageKey;
    this.sessionShouldRefreshFunc = sessionShouldRefresh;
    this.cookieService = cookieService;
  }

  async setSession(session: T | undefined): Promise<void> {
    this.currentSession = session;
    await this.saveSession(session);
  }

  async getSession(options: GetSessionOptions): Promise<T | undefined> {
    const storedSession = await this.loadSession();
    if (!this.currentSession && storedSession) {
      this.currentSession = storedSession;
    }

    if (this.currentSession) {
      const shouldRefresh = this.sessionShouldRefreshFunc(this.currentSession!);
      if (!shouldRefresh) {
        return this.currentSession!;
      }

      try {
        const refreshedSession = await this.collapsedSessionRefresh();
        await this.setSession(refreshedSession);
        return refreshedSession;
      } catch (error) {
        await this.removeSession();
        return undefined;
      }
    }

    // If we continue here we will show a popup, so exit if this is an optional session request.
    if (options.optional) {
      return undefined;
    }

    // We can call authRequester multiple times, the returned session will contain all requested scopes.
    const newSession = await this.connector.createSession(options);
    await this.setSession(newSession);
    this.stateTracker.setIsSignedIn(true);
    return this.currentSession;
  }

  async removeSession() {
    await this.setSession(undefined);
    if (this.cookieService) {
      await this.cookieService.removeSession(this.storageKey);
    } else {
      localStorage.removeItem(this.storageKey);
    }
    await this.connector.removeSession();
    this.stateTracker.setIsSignedIn(false);
  }

  sessionState$() {
    return this.stateTracker.sessionState$();
  }

  private async collapsedSessionRefresh(): Promise<T> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.connector.refreshSession(this.currentSession!);

    try {
      const session = await this.refreshPromise;
      this.stateTracker.setIsSignedIn(true);
      return session;
    } finally {
      delete this.refreshPromise;
    }
  }

  private async loadSession(): Promise<T | undefined> {
    if (this.cookieService) {
      try {
        return await this.cookieService.loadSession<T>(this.storageKey);
      } catch (error) {
        // If cookie service fails, try to remove any corrupted session
        try {
          await this.cookieService.removeSession(this.storageKey);
        } catch (removeError) {
          // Ignore removal errors
        }
        return undefined;
      }
    } else {
      // Fallback to localStorage
      try {
        const sessionJson = localStorage.getItem(this.storageKey);
        if (sessionJson) {
          const session = JSON.parse(sessionJson, (_key, value) => {
            if (value?.__type === 'Set') {
              return new Set(value.__value);
            }
            return value;
          });
          return session;
        }

        return undefined;
      } catch (error) {
        localStorage.removeItem(this.storageKey);
        return undefined;
      }
    }
  }

  private async saveSession(session: T | undefined): Promise<void> {
    if (this.cookieService) {
      if (session === undefined) {
        await this.cookieService.removeSession(this.storageKey);
      } else {
        await this.cookieService.saveSession(this.storageKey, session);
      }
    } else {
      // Fallback to localStorage
      if (session === undefined) {
        localStorage.removeItem(this.storageKey);
        return;
      }

      localStorage.setItem(
        this.storageKey,
        JSON.stringify(session, (_key, value) => {
          if (value instanceof Set) {
            return {
              __type: 'Set',
              __value: Array.from(value),
            };
          }
          return value;
        }),
      );
    }
  }
}

export class LdapRefreshingAuthSessionManager<
  T,
> extends RefreshingAuthSessionManager<T> {}

export class GuestUsersRefreshingAuthSessionManager extends LdapRefreshingAuthSessionManager<GuestUsersAuthSession> {}
